import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  Search,
  CornerDownLeft,
  Plus,
  LayoutDashboard,
  FolderOpen,
  CheckSquare,
  Calendar,
  GanttChart,
  Package,
  Clock,
  GitBranch,
  Users,
  Calculator,
  FileText,
  TrendingUp,
  BarChart3,
  Settings,
  type LucideIcon,
} from 'lucide-react';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateTask: () => void;
}

interface Cmd {
  id: string;
  label: string;
  group: string;
  icon: LucideIcon;
  run: () => void;
  accent?: boolean;
}

export default function CommandPalette({ isOpen, onClose, onCreateTask }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [projectHits, setProjectHits] = useState<Cmd[]>([]);
  const [taskHits, setTaskHits] = useState<Cmd[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  const staticCommands: Cmd[] = useMemo(
    () => [
      { id: 'new-task', label: 'Crear tarea', group: 'Acciones', icon: Plus, run: onCreateTask, accent: true },
      { id: 'new-project', label: 'Nuevo proyecto', group: 'Acciones', icon: FolderOpen, run: () => go('/projects') },
      { id: 'new-budget', label: 'Nuevo presupuesto', group: 'Acciones', icon: Calculator, run: () => go('/budgets') },
      { id: 'nav-dashboard', label: 'Dashboard', group: 'Ir a', icon: LayoutDashboard, run: () => go('/dashboard') },
      { id: 'nav-projects', label: 'Proyectos', group: 'Ir a', icon: FolderOpen, run: () => go('/projects') },
      { id: 'nav-tasks', label: 'Tareas', group: 'Ir a', icon: CheckSquare, run: () => go('/tasks') },
      { id: 'nav-calendar', label: 'Calendario', group: 'Ir a', icon: Calendar, run: () => go('/calendar') },
      { id: 'nav-gantt', label: 'Timeline', group: 'Ir a', icon: GanttChart, run: () => go('/gantt') },
      { id: 'nav-equipment', label: 'Material', group: 'Ir a', icon: Package, run: () => go('/equipment') },
      { id: 'nav-time', label: 'Tiempo', group: 'Ir a', icon: Clock, run: () => go('/time-tracking') },
      { id: 'nav-pipeline', label: 'Pipeline', group: 'Ir a', icon: GitBranch, run: () => go('/pipeline') },
      { id: 'nav-clients', label: 'Clientes', group: 'Ir a', icon: Users, run: () => go('/clients') },
      { id: 'nav-budgets', label: 'Presupuestos', group: 'Ir a', icon: Calculator, run: () => go('/budgets') },
      { id: 'nav-invoices', label: 'Facturas', group: 'Ir a', icon: FileText, run: () => go('/invoices') },
      { id: 'nav-profitability', label: 'Rentabilidad', group: 'Ir a', icon: TrendingUp, run: () => go('/profitability') },
      { id: 'nav-forecast', label: 'Forecast', group: 'Ir a', icon: BarChart3, run: () => go('/forecast') },
      { id: 'nav-settings', label: 'Configuración', group: 'Ir a', icon: Settings, run: () => go('/settings') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Búsqueda en vivo de proyectos y tareas (debounced)
  useEffect(() => {
    const q = query.trim();
    if (!isOpen || !user || q.length < 2) {
      setProjectHits([]);
      setTaskHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const [projs, tks] = await Promise.all([
        supabase
          .from('apro_projects')
          .select('id, nombre')
          .eq('created_by', user.id)
          .ilike('nombre', `%${q}%`)
          .limit(6),
        supabase
          .from('apro_tasks')
          .select('id, nombre')
          .eq('created_by', user.id)
          .ilike('nombre', `%${q}%`)
          .limit(6),
      ]);
      if (cancelled) return;
      setProjectHits(
        (projs.data || []).map((p: any) => ({
          id: `proj-${p.id}`,
          label: p.nombre || 'Proyecto sin nombre',
          group: 'Proyectos',
          icon: FolderOpen,
          run: () => go(`/projects?open=${p.id}`),
        }))
      );
      setTaskHits(
        (tks.data || []).map((t: any) => ({
          id: `task-${t.id}`,
          label: t.nombre || 'Tarea sin nombre',
          group: 'Tareas',
          icon: CheckSquare,
          run: () => go(`/tasks?open=${t.id}`),
        }))
      );
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isOpen, user]);

  const results: Cmd[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return staticCommands;
    const matchedStatic = staticCommands.filter(
      (c) => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q)
    );
    return [...projectHits, ...taskHits, ...matchedStatic];
  }, [query, projectHits, taskHits, staticCommands]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    setActive(0);
  }, [query, projectHits, taskHits]);

  if (!isOpen) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      results[active]?.run();
    }
  };

  let lastGroup = '';

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-3" onKeyDown={onKeyDown}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mt-[12vh] w-full max-w-xl bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden animate-fade-in-up">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-800">
          <Search className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Crear, buscar tareas y proyectos, o ir a…"
            className="flex-1 bg-transparent text-gray-100 placeholder-gray-500 text-[15px] focus:outline-none"
          />
          <span className="text-[10px] text-gray-500 bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 font-mono">
            ESC
          </span>
        </div>

        <div className="max-h-80 overflow-y-auto p-2">
          {results.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-gray-500">Sin resultados</div>
          )}
          {results.map((c, i) => {
            const Icon = c.icon;
            const showGroup = c.group !== lastGroup;
            lastGroup = c.group;
            const isActive = i === active;
            return (
              <React.Fragment key={c.id}>
                {showGroup && (
                  <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-600 font-semibold">
                    {c.group}
                  </div>
                )}
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => c.run()}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isActive ? 'bg-gray-800' : ''
                  }`}
                >
                  <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${c.accent ? 'text-brand-400' : 'text-gray-400'}`} />
                  <span className={`flex-1 text-[13.5px] truncate ${c.accent ? 'text-gray-100 font-medium' : 'text-gray-200'}`}>
                    {c.label}
                  </span>
                  {isActive && <CornerDownLeft className="w-4 h-4 text-gray-500" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
